import type { FieldExplainerContent } from './types';

export type CoastFireExplainerContext = {
  input: {
    currentAge: number;
    retirementAge: number;
    currentSavings: number;
    targetMethod: 'expenses' | 'number';
    annualExpenses: number;
    withdrawalRate: number;
    targetFIRENumber: number;
    expectedReturn: number;
    inflationRate: number;
  };
  result: {
    yearsToRetirement: number;
    realReturn: number;
    calculatedFIRENumber: number;
    coastFIRENumber: number;
    projectedBalance: number;
    isCoastFIRE: boolean;
    monthlySavingsNeeded: number;
    savingsWindowMonths: number;
    hasSavingsWindow: boolean;
  };
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const usdCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

export function getCoastFireFieldExplainers(ctx: CoastFireExplainerContext): FieldExplainerContent[] {
  const {
    currentAge,
    retirementAge,
    currentSavings,
    targetMethod,
    annualExpenses,
    targetFIRENumber,
    expectedReturn,
    inflationRate
  } = ctx.input;

  const {
    yearsToRetirement,
    realReturn,
    calculatedFIRENumber,
    coastFIRENumber,
    projectedBalance,
    isCoastFIRE,
    monthlySavingsNeeded,
    savingsWindowMonths,
    hasSavingsWindow
  } = ctx.result;

  const usingExpenses = targetMethod === 'expenses';
  // The engine substitutes 4% for a non-positive withdrawal rate; mirror that
  // here so no formula ever divides by the raw zero.
  const withdrawalRate = ctx.input.withdrawalRate > 0 ? ctx.input.withdrawalRate : 4;
  const usingFallbackRate = withdrawalRate !== ctx.input.withdrawalRate;
  const realRate = realReturn / 100;
  const growthFactor = Math.pow(1 + realRate, yearsToRetirement);
  const savingsYears = Math.min(10, yearsToRetirement / 2);
  const savingsMonths = savingsYears * 12;
  const coastYearsAfterWindow = Math.max(0, yearsToRetirement - savingsYears);
  const thresholdAtWindowEnd = calculatedFIRENumber / Math.pow(1 + realRate, coastYearsAfterWindow);
  const shortfall = coastFIRENumber - currentSavings;
  const coverage = coastFIRENumber > 0 ? (currentSavings / coastFIRENumber) * 100 : 0;

  return [
    {
      id: 'currentAge',
      kind: 'input',
      label: 'Current age',
      plain:
        realRate > 0
          ? 'The date the coast number is measured at. "Coast FIRE" is always a statement about right now, so moving this age moves the threshold — every year you wait, the number you need on hand goes up.'
          : 'The date the coast number is measured at. "Coast FIRE" is always a statement about right now, so moving this age moves the threshold. With your real return at or below zero, waiting does not raise the number you need — money that cannot outgrow inflation has nothing to gain from extra years.',
      formula: `years of coasting = ${retirementAge} − ${currentAge} = ${yearsToRetirement}\n\nthreshold at ${currentAge}: ${usd.format(coastFIRENumber)}\nthreshold at ${currentAge + 1}: ${usd.format(calculatedFIRENumber / Math.pow(1 + realRate, Math.max(0, yearsToRetirement - 1)))}`,
      feeds: 'The discount period behind the coast number, and the left edge of the chart.'
    },
    {
      id: 'retirementAge',
      kind: 'input',
      label: 'Retirement age',
      plain:
        realRate > 0
          ? 'When you want the target hit. It is the only thing separating a big coast number from a small one — the same target, further away, needs far less on hand today.'
          : 'When you want the target hit. Normally more distance shrinks the coast number, but with your real return at or below zero the extra years do no compounding work — pushing retirement out does not reduce what you need on hand today.',
      formula: `${usd.format(calculatedFIRENumber)} discounted back ${yearsToRetirement} years\n  at ${realReturn.toFixed(4)}% real\n  = ${usd.format(coastFIRENumber)}`,
      feeds: 'The right edge of the chart, the coast number, and the projected balance.',
      assumption:
        'The model stops here. It does not check whether the target survives the retirement itself, only that you arrive with it.'
    },
    {
      id: 'currentSavings',
      kind: 'input',
      label: 'Current savings',
      plain:
        'Everything invested today. This is the whole of the coast test: it is compared against the coast number, and it is the only money the projection ever grows.',
      formula: `${usd.format(currentSavings)} vs ${usd.format(coastFIRENumber)} needed\n  = ${coverage.toFixed(1)}% of the threshold\n\n${usd.format(currentSavings)} × ${growthFactor.toFixed(4)}\n  = ${usd.format(projectedBalance)} at ${retirementAge}`,
      feeds: 'The projected balance, the coast/not-coast verdict, and the solid line on the chart.',
      assumption:
        'Treated as one pot growing at one rate. No split between taxable and sheltered accounts, and no tax taken on the way out.'
    },
    {
      id: 'expectedReturn',
      kind: 'input',
      label: 'Expected return',
      plain:
        'Your nominal annual return, before inflation is taken out. It is never used on its own — it is immediately combined with your inflation rate into a single real rate that drives everything else.',
      formula: `${expectedReturn}% nominal, then deflated by ${inflationRate}%\n  → ${realReturn.toFixed(4)}% real\n\nthat real rate compounds for ${yearsToRetirement} years:\n  ${growthFactor.toFixed(4)}×`,
      feeds: 'Both the projected balance and the coast number — and it moves them in opposite directions.',
      assumption:
        'A higher return cuts the coast number and lifts the projected balance at the same time, so this input flatters the verdict twice over. Treat an optimistic figure here with more suspicion than anywhere else on the page.'
    },
    {
      id: 'inflationRate',
      kind: 'input',
      label: 'Inflation rate',
      plain:
        'What you expect prices to do. It is divided out of your return rather than subtracted, which is the exact form, and it is what makes every dollar on this page a dollar of today.',
      formula: `real = (1 + ${expectedReturn}%) / (1 + ${inflationRate}%) − 1\n     = ${realReturn.toFixed(4)}%\n\nthe rough version, ${expectedReturn} − ${inflationRate} = ${(expectedReturn - inflationRate).toFixed(2)}%,\nis ${Math.abs(expectedReturn - inflationRate - realReturn).toFixed(4)} points off`,
      feeds: 'The real return card, and through it every balance and threshold here.',
      assumption:
        'Because inflation is removed up front, the coast number and projected balance are in today’s purchasing power. That is the opposite convention to most of the other calculators on this site, which report nominal future dollars.'
    },
    {
      id: 'withdrawalRate',
      kind: 'input',
      label: 'Withdrawal rate',
      plain: usingExpenses
        ? 'The share of the portfolio you plan to spend each year in retirement. It converts your annual expenses into the portfolio size that supports them.'
        : 'The share of the portfolio you plan to spend each year. It is idle right now, because your target method is set to a direct FIRE number rather than expenses.',
      formula: usingExpenses
        ? `${usd.format(annualExpenses)} / ${withdrawalRate}%\n  = ${usd.format(calculatedFIRENumber)}\n\n(that is ${(100 / withdrawalRate).toFixed(1)}× your annual spending)`
        : `not used while the target method is a direct number\n\nif it were: ${usd.format(annualExpenses)} / ${withdrawalRate}% = ${usd.format(annualExpenses / (withdrawalRate / 100))}`,
      feeds: usingExpenses
        ? 'The FIRE number, and therefore the coast number and the dashed target line.'
        : 'Nothing at the moment. Switch the target method to expenses to bring it back into play.',
      assumption: usingFallbackRate
        ? 'The rate entered is zero or negative, which cannot fund any spending, so the engine substitutes its 4% default for every figure on this page until a positive rate is entered.'
        : 'A rate you pick, not a rate the model tests. Nothing here checks whether that withdrawal survives a bad first decade — this page only asks whether you arrive with the money.'
    },
    {
      id: 'targetMethod',
      kind: 'input',
      label: 'Target method',
      plain: usingExpenses
        ? 'Chooses which of the two target inputs is live. Set to expenses, so your FIRE number is built from annual spending and the withdrawal rate, and the direct target field is ignored.'
        : 'Chooses which of the two target inputs is live. Set to a direct number, so your FIRE number is taken straight from the direct target field, and annual expenses and the withdrawal rate are ignored.',
      formula: usingExpenses
        ? `FIRE number = ${usd.format(annualExpenses)} / ${withdrawalRate}%\n            = ${usd.format(calculatedFIRENumber)}\n\n(direct target of ${usd.format(targetFIRENumber)} unused)`
        : `FIRE number = ${usd.format(targetFIRENumber)}\n\n(expenses and withdrawal rate unused)`,
      feeds: 'The single FIRE number everything on this page is discounted back from.'
    },
    {
      id: 'annualExpenses',
      kind: 'input',
      label: 'Annual expenses',
      plain: usingExpenses
        ? 'What you expect a year of retirement to cost, in today’s money. Divided by your withdrawal rate, it becomes the portfolio you are aiming at.'
        : 'What you expect a year of retirement to cost. It is inactive while the target method is set to a direct FIRE number.',
      formula: usingExpenses
        ? `${usd.format(annualExpenses)} / ${withdrawalRate}% = ${usd.format(calculatedFIRENumber)}\n\nevery ${usd.format(1000)} of yearly spending adds\n${usd.format(1000 / (withdrawalRate / 100))} to the target`
        : `inactive — the target is fixed at ${usd.format(targetFIRENumber)}`,
      feeds: usingExpenses ? 'The FIRE number and everything discounted from it.' : 'Nothing while the direct-number method is selected.',
      assumption:
        'Read as a real, inflation-adjusted figure, matching the real return used everywhere else. Enter what your life costs today, not what you guess it will cost in decades.'
    },
    {
      id: 'targetFIRENumber',
      kind: 'input',
      label: 'Direct FIRE target',
      plain: usingExpenses
        ? 'A portfolio size you name outright, skipping the expenses-and-withdrawal-rate arithmetic. It is inactive while the target method is set to expenses.'
        : 'A portfolio size you name outright. It is used exactly as typed — no withdrawal rate is applied to it and no sanity check is made against your spending.',
      formula: usingExpenses
        ? `inactive — the target is ${usd.format(annualExpenses)} / ${withdrawalRate}%\n  = ${usd.format(calculatedFIRENumber)}`
        : `FIRE number = ${usd.format(targetFIRENumber)}\ncoast number = ${usd.format(targetFIRENumber)} / ${growthFactor.toFixed(4)}\n             = ${usd.format(coastFIRENumber)}`,
      feeds: usingExpenses ? 'Nothing while the expenses method is selected.' : 'The coast number, the dashed target line, and the verdict.'
    },
    {
      id: 'coastFIRENumber',
      kind: 'result',
      label: 'Required coast number',
      plain: `The balance you would need today for compounding alone to reach ${usd.format(calculatedFIRENumber)} by ${retirementAge}, with no further contributions. It is the FIRE number run backwards through the real rate.`,
      formula: `${usd.format(calculatedFIRENumber)} / (1 + ${realRate.toFixed(6)})^${yearsToRetirement}\n  = ${usd.format(coastFIRENumber)}\n\nyou have ${usd.format(currentSavings)}${shortfall > 0 ? `, short by ${usd.format(shortfall)}` : `, ahead by ${usd.format(-shortfall)}`}`,
      feeds: 'The coast/not-coast verdict and the left-hand end of the dashed target line.'
    },
    {
      id: 'projectedBalance',
      kind: 'result',
      label: 'Projected balance',
      plain: `Where today’s savings land at ${retirementAge} if you never add another dollar — the whole premise of coasting. Nothing on this page ever adds contributions to this line.`,
      formula: `${usd.format(currentSavings)} × (1 + ${realRate.toFixed(6)})^${yearsToRetirement}\n  = ${usd.format(projectedBalance)}\n\nagainst a target of ${usd.format(calculatedFIRENumber)}\n  = ${calculatedFIRENumber > 0 ? ((projectedBalance / calculatedFIRENumber) * 100).toFixed(1) : '0.0'}%`,
      feeds: 'The solid line on the chart and the projection table.',
      assumption:
        'In today’s dollars, because the real rate already has inflation removed. It also assumes you leave it entirely alone — no withdrawals, no job change that cashes out an account.'
    },
    {
      id: 'currentStatus',
      kind: 'result',
      label: 'Current status',
      plain: isCoastFIRE
        ? `Your savings already clear the coast number, so no further contributions are required to reach ${usd.format(calculatedFIRENumber)} by ${retirementAge}.`
        : hasSavingsWindow
          ? `You are below the coast threshold, so the card solves for a monthly deposit. The tricky part is that the threshold itself climbs while you save — every year spent saving is a year the money no longer has to compound.`
          : 'You are below the threshold with no saving years left — retirement age has arrived. There is no monthly plan to solve for; the gap between your savings and the target is due in full, now.',
      formula: isCoastFIRE
        ? `${usd.format(currentSavings)} ≥ ${usd.format(coastFIRENumber)}\n  → already coasting`
        : hasSavingsWindow
          ? `savings window = min(10, ${yearsToRetirement}/2) = ${savingsYears} years\nthreshold at age ${currentAge + savingsYears}\n  = ${usd.format(calculatedFIRENumber)} / (1+r)^${coastYearsAfterWindow}\n  = ${usd.format(thresholdAtWindowEnd)}\n\nsolve for pmt over ${savingsWindowMonths} months at r/12:\n  ${usdCents.format(monthlySavingsNeeded)} per month for ${savingsWindowMonths} months`
          : `${usd.format(currentSavings)} < ${usd.format(coastFIRENumber)}\nyears left to save = 0\n\ngap due immediately:\n  ${usd.format(shortfall)}`,
      feeds: 'Nothing downstream. It is the page’s recommendation, read off the same target line the chart draws.',
      assumption: isCoastFIRE
        ? 'Clearing the threshold today does not make it permanent. A weaker return than you assumed pulls you back below it.'
        : hasSavingsWindow
          ? `The ${savingsYears}-year window is the model’s own choice — half your remaining time, capped at ten years — not the fastest possible route. Save more per month and you get there sooner.`
          : undefined
    },
    {
      id: 'realReturn',
      kind: 'result',
      label: 'Real return',
      plain:
        'Your expected return with inflation divided out. This is the only rate that actually compounds anywhere on this page; the nominal return you typed is never used directly.',
      formula: `(1 + ${expectedReturn}/100) / (1 + ${inflationRate}/100) − 1\n  = ${realRate.toFixed(6)}\n  = ${realReturn.toFixed(4)}%\n\nover ${yearsToRetirement} years that is ${growthFactor.toFixed(4)}× growth`,
      feeds: 'The coast number, the projected balance, both chart lines, and the monthly savings figure.',
      assumption:
        'Because it is a real rate, every dollar on this page is in today’s purchasing power. Set expected return equal to inflation and it goes to zero: the projection flatlines, which is the honest answer rather than a rounding artifact.'
    }
  ];
}
